import React from 'react';
import Account from 'models/Account'
import LocalStorage from 'services/LocalStorage'
import AccountService from 'services/AccountService'
import NativeInterface from 'services/NativeInterface'
import SettingsService from 'services/SettingsService'
import AnalyticsService from 'services/AnalyticsService'

interface IAuthContext {
    account: Account;
    updateProperties(props: any): void,
    markFavorite(dimId: string, val: boolean): void,
    enableDimension(dimId: string, val: boolean): void,
    setDimensionEnv(dimId: string, val: string): void,
    makeFileUrl(dimId: string, file: string): string,
    logAnalyticsEvent(event: string, params: any): void,
}

const AuthContext = React.createContext<IAuthContext>({
    account: new Account(null),
    updateProperties: (props: any) => { },
    markFavorite: (dimId: string, val: boolean) => { },
    enableDimension: (dimId: string, val: boolean) => { },
    setDimensionEnv: (dimId: string, val: string) => { },
    makeFileUrl: (dimId: string, file: string) => { return '' },
    logAnalyticsEvent: (event: string, params: any) => { },
});

export const AuthContextProvider: React.FC = (props) => {
    const [account, setAccount] = React.useState<Account>(AccountService.currentAccount());
    const CURRENT_USER_KEY = AccountService.STORAGE_PATH;

    React.useEffect(() => {
        AccountService.subscribeOnAccountChange(acc => setAccount(acc))
        NativeInterface.registerCallback('reloadAccount', () => {
            console.log("Reloading account callback")
        })
      }, []);

    async function updateProperties(props: any) {
        console.log(`Update account props: ${JSON.stringify(props)}`)
        account.updateProperties(props);
        LocalStorage.set(CURRENT_USER_KEY, account.rawData);
        if (account.valid()) {
            return AccountService.updateCurrentAccountProperties(props)
        }
        return {}
    }

    function markFavorite(dimId: string, val: boolean) {
        account.markFavorite(dimId, val);
        LocalStorage.set(CURRENT_USER_KEY, account.rawData);
        if (account.valid()) {
            AccountService.updateCurrentAccountDimensions(account.getDimensionsRecords());
        }
    }

    function enableDimension(dimId: string, val: boolean) {
        console.log(`enableDimension: ${dimId} ${val}`);
        account.enableDimension(dimId, val);
        LocalStorage.set(CURRENT_USER_KEY, account.rawData);
        if (account.valid()) {
            AccountService.updateCurrentAccountDimensions(account.getDimensionsRecords());
        }
    }

    function setDimensionEnv(dimId: string, val: string) {
        console.log(`setDimensionEnv: ${dimId} ${val}`);
        account.setDimensionEnv(dimId, val);
        LocalStorage.set(CURRENT_USER_KEY, account.rawData);
        if (account.valid()) {
            AccountService.updateCurrentAccountDimensions(account.getDimensionsRecords());
        }
    }

    function makeFileUrl(dimId: string, file: string) {
        return SettingsService.makeFileUrl(dimId, account.getDimensionEnv(dimId), file)
    }

    function logAnalyticsEvent(event: string, params: any) {
        params.name = event
        if (params.dimension) {
            params.env = account.getDimensionEnv(params.dimension)
        }
        params.app_instance = NativeInterface.getAppInstanceId()
        AnalyticsService.logEvent(params)
    }

    const contextValue: IAuthContext = {
        account: account,
        updateProperties: updateProperties,
        markFavorite: markFavorite,
        enableDimension: enableDimension,
        setDimensionEnv: setDimensionEnv,
        makeFileUrl: makeFileUrl,
        logAnalyticsEvent: logAnalyticsEvent,
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {props.children}
        </AuthContext.Provider>
    );
};

export default AuthContext;